import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import type { WizardType } from '../store/wizardStore';
import { useWizardStore } from '../store/wizardStore';
import { syncSession } from '../services/session';
import {
  renderPlanPng,
  loadImage,
  type ExportDivision,
  type ExportFloor,
} from '../lib/planExport';

// Fase B — 2D floor plan. Upload a plan image per floor (multi-floor for
// Moradia), drag the opted-in divisions onto it, and export a PNG map with
// numbered markers + device legend for the montador. Plan images live in
// IndexedDB (`syhouse:plan:<floorId>`); placements live in the wizard store.

const PLAN_KEY = (floorId: string) => `syhouse:plan:${floorId}`;
const MAX_PLAN_DIM = 1600;

// Downscale + JPEG-encode the uploaded plan so IndexedDB and the export
// canvas stay light even with phone photos.
async function compressImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, MAX_PLAN_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function clamp01(v: number): number {
  return Math.min(0.98, Math.max(0.02, v));
}

export function FloorPlanPage() {
  const { type } = useParams<{ type: WizardType }>();
  const navigate = useNavigate();
  const {
    sessionId, accessToken, wizardType, divisions, selections, devices,
    floors, activeFloorId,
    addFloor, removeFloor, setActiveFloor, setFloorHasPlan,
    setPlacement, removePlacement, setSessionId, setAccessToken,
  } = useWizardStore();

  const isMoradia = type === 'Moradia';
  const optedIn = useMemo(() => divisions.filter((d) => d.optedIn), [divisions]);

  // Always have at least one floor ("Planta" for apartments, "R/C" for Moradia).
  // Read the live store inside the effect — StrictMode double-runs effects and
  // the render-scoped `floors` would create a duplicate floor.
  useEffect(() => {
    if (useWizardStore.getState().floors.length === 0) {
      addFloor(isMoradia ? 'R/C' : 'Planta');
    }
  }, [floors.length, addFloor, isMoradia]);

  const floor = floors.find((f) => f.id === activeFloorId) ?? floors[0] ?? null;

  // ── plan image for the active floor ──
  const [planUrl, setPlanUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!floor) return;
    if (!floor.hasPlan) {
      setPlanUrl(null);
      return;
    }
    let cancelled = false;
    void idbGet<string>(PLAN_KEY(floor.id)).then((v) => {
      if (!cancelled) setPlanUrl(v ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [floor?.id, floor?.hasPlan]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleUpload(file: File) {
    if (!floor) return;
    const dataUrl = await compressImage(file);
    await idbSet(PLAN_KEY(floor.id), dataUrl);
    setFloorHasPlan(floor.id, true);
    setPlanUrl(dataUrl);
  }

  function handleRemoveFloor(floorId: string) {
    void idbDel(PLAN_KEY(floorId));
    removeFloor(floorId);
  }

  // ── marker numbering + legend (shared by preview and export) ──
  const numberOf = useMemo(
    () => new Map(optedIn.map((d, i) => [d.id, i + 1])),
    [optedIn],
  );

  const legendLines = useMemo(() => {
    const lines = new Map<string, string[]>();
    for (const d of optedIn) {
      const combos = devices[d.id] ?? [];
      const comboSkus = new Set(combos.flatMap((c) => c.skus));
      const extra = (selections[d.id] ?? []).filter((sku) => !comboSkus.has(sku)).length;
      const out = combos.map((c) => c.label);
      if (extra > 0) out.push(`${extra} ${extra === 1 ? 'outro componente' : 'outros componentes'}`);
      lines.set(d.id, out);
    }
    return lines;
  }, [optedIn, devices, selections]);

  // Division placed on any floor → chip disappears from the "por colocar" rail.
  const placedAnywhere = useMemo(
    () => new Set(floors.flatMap((f) => Object.keys(f.placements))),
    [floors],
  );
  const unplaced = optedIn.filter((d) => !placedAnywhere.has(d.id));

  // ── drag handling ──
  const planRef = useRef<HTMLDivElement>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  function posFromPointer(e: { clientX: number; clientY: number }) {
    const rect = planRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  }

  function handlePlanPointerMove(e: React.PointerEvent) {
    if (!dragId || !floor) return;
    const pos = posFromPointer(e);
    if (pos) setPlacement(floor.id, dragId, pos);
  }

  useEffect(() => {
    if (!dragId) return;
    const stop = () => setDragId(null);
    window.addEventListener('pointerup', stop);
    return () => window.removeEventListener('pointerup', stop);
  }, [dragId]);

  // ── export ──
  const [exporting, setExporting] = useState(false);
  const [saveLead, setSaveLead] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const exportFloors: ExportFloor[] = [];
      for (const f of floors) {
        const hasContent = f.hasPlan || Object.keys(f.placements).length > 0;
        if (!hasContent) continue;
        let img: HTMLImageElement | null = null;
        if (f.hasPlan) {
          const dataUrl = await idbGet<string>(PLAN_KEY(f.id));
          if (dataUrl) img = await loadImage(dataUrl);
        }
        const divMap = new Map<string, ExportDivision>();
        for (const divId of Object.keys(f.placements)) {
          const div = optedIn.find((d) => d.id === divId);
          const n = numberOf.get(divId);
          if (!div || !n) continue;
          divMap.set(divId, { number: n, label: div.label, lines: legendLines.get(divId) ?? [] });
        }
        exportFloors.push({ floor: f, planImage: img, divisions: divMap });
      }

      const legend: ExportDivision[] = optedIn.map((d) => ({
        number: numberOf.get(d.id) ?? 0,
        label: d.label,
        lines: legendLines.get(d.id) ?? [],
      }));

      const blob = await renderPlanPng(
        exportFloors,
        legend,
        `Planta Smartify — ${wizardType ?? type ?? ''}`,
      );

      // Remarketing lead — only with explicit consent (RGPD).
      if (saveLead) {
        try {
          const synced = await syncSession({ sessionId, accessToken, wizardType, divisions, selections, devices });
          setSessionId(synced.sessionId);
          setAccessToken(synced.accessToken);
        } catch {
          // Lead save must never block the download.
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `planta-smartify-${(wizardType ?? 'casa').toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Não foi possível gerar o mapa. Tenta novamente.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate(`/wizard/${type}`)}
          className="mb-4 text-sm text-smartify-muted hover:text-smartify-ink"
        >
          ← Voltar às divisões
        </button>
        <h1 className="text-xl font-bold text-smartify-ink">Planta da casa</h1>
        <p className="mt-1 text-sm text-smartify-muted">
          Carrega a planta{isMoradia ? ' de cada piso' : ''}, arrasta as divisões
          para o sítio certo e descarrega o mapa para dar ao montador. Opcional.
        </p>
      </div>

      {/* Floor selector (multi-piso para Moradia) */}
      {isMoradia && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {floors.map((f) => {
            const active = f.id === floor?.id;
            return (
              <span key={f.id} className="inline-flex items-center">
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveFloor(f.id)}
                  className={[
                    'rounded-l-full border px-3 py-1.5 text-sm font-medium transition-colors',
                    floors.length > 1 ? '' : 'rounded-r-full',
                    active
                      ? 'border-smartify-brand bg-smartify-brand text-white'
                      : 'border-smartify-line bg-white text-smartify-ink hover:border-smartify-accent',
                  ].join(' ')}
                >
                  {f.label}
                </button>
                {floors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFloor(f.id)}
                    aria-label={`Remover ${f.label}`}
                    className={[
                      'rounded-r-full border border-l-0 px-2 py-1.5 text-sm transition-colors',
                      active
                        ? 'border-smartify-brand bg-smartify-brand text-white/80 hover:text-white'
                        : 'border-smartify-line bg-white text-smartify-muted hover:text-smartify-danger',
                    ].join(' ')}
                  >
                    ✕
                  </button>
                )}
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => addFloor()}
            className="rounded-full border border-dashed border-smartify-line px-3 py-1.5 text-sm text-smartify-muted hover:border-smartify-accent hover:text-smartify-accent"
          >
            + Andar
          </button>
        </div>
      )}

      {/* Plan canvas */}
      <div
        ref={planRef}
        onPointerMove={handlePlanPointerMove}
        className="relative w-full select-none overflow-hidden rounded-smartify border border-smartify-line bg-smartify-paper"
        style={{ touchAction: dragId ? 'none' : 'auto' }}
      >
        {planUrl ? (
          <img src={planUrl} alt={`Planta — ${floor?.label ?? ''}`} className="block w-full" draggable={false} />
        ) : (
          <div className="flex aspect-[5/3] w-full flex-col items-center justify-center gap-2 text-sm text-smartify-muted">
            <span className="text-3xl" aria-hidden>🗺️</span>
            Sem planta carregada — podes posicionar as divisões na mesma.
          </div>
        )}

        {floor &&
          Object.entries(floor.placements).map(([divId, pos]) => {
            const div = optedIn.find((d) => d.id === divId);
            const n = numberOf.get(divId);
            if (!div || !n) return null;
            return (
              <div
                key={divId}
                className="absolute"
                style={{
                  left: `${pos.x * 100}%`,
                  top: `${pos.y * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  touchAction: 'none',
                }}
              >
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    setDragId(divId);
                  }}
                  aria-label={`Mover ${div.label}`}
                  className={[
                    'flex h-9 w-9 cursor-grab items-center justify-center rounded-full border-2 border-white text-sm font-bold text-white shadow-card',
                    dragId === divId ? 'bg-smartify-accent' : 'bg-smartify-brand',
                  ].join(' ')}
                >
                  {n}
                </button>
                <button
                  type="button"
                  onClick={() => floor && removePlacement(floor.id, divId)}
                  aria-label={`Retirar ${div.label} da planta`}
                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-smartify-ink text-[9px] leading-none text-white"
                >
                  ✕
                </button>
              </div>
            );
          })}
      </div>

      {/* Upload */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload(file);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="rounded-smartify border border-smartify-line bg-white px-4 py-2 text-sm font-medium text-smartify-ink hover:border-smartify-accent"
        >
          📷 {floor?.hasPlan ? 'Substituir planta' : 'Carregar planta'}
          {isMoradia && floor ? ` (${floor.label})` : ''}
        </button>
      </div>

      {/* Unplaced divisions */}
      {unplaced.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
            Por colocar — toca para adicionar à planta
          </p>
          <div className="flex flex-wrap gap-2">
            {unplaced.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => floor && setPlacement(floor.id, d.id, { x: 0.5, y: 0.5 })}
                className="inline-flex items-center gap-1.5 rounded-full border border-smartify-line bg-white px-3 py-1.5 text-sm text-smartify-ink hover:border-smartify-accent"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-smartify-brand text-[10px] font-bold text-white">
                  {numberOf.get(d.id)}
                </span>
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Legend preview */}
      <div className="mt-6 rounded-smartify border border-smartify-line bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-smartify-ink">Legenda</h2>
        <ul className="space-y-2">
          {optedIn.map((d) => (
            <li key={d.id} className="flex gap-3 text-sm">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-smartify-brand text-[10px] font-bold text-white">
                {numberOf.get(d.id)}
              </span>
              <div className="min-w-0">
                <p className="font-medium text-smartify-ink">{d.label}</p>
                {(legendLines.get(d.id) ?? []).map((line, i) => (
                  <p key={i} className="text-xs text-smartify-muted">· {line}</p>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Export + lead consent */}
      <div className="mt-6 space-y-3">
        <label className="flex items-start gap-2 text-sm text-smartify-ink">
          <input
            type="checkbox"
            checked={saveLead}
            onChange={(e) => setSaveLead(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-smartify-line text-smartify-brand focus:ring-smartify-brand"
          />
          <span>
            Guardar a minha configuração no servidor para a Smartify me poder
            contactar com uma proposta.
            <span className="block text-xs text-smartify-muted">
              Opcional — sem isto, tudo fica apenas neste browser.
            </span>
          </span>
        </label>

        {exportError && (
          <div className="rounded-smartify border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {exportError}
          </div>
        )}

        <button
          type="button"
          disabled={exporting}
          onClick={handleExport}
          className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {exporting ? 'A gerar mapa…' : '⬇️ Descarregar mapa (PNG)'}
        </button>
        <p className="text-center text-xs text-smartify-muted">
          Mapa com as divisões numeradas e a lista de aparelhos — ideal para dar ao montador.
        </p>
      </div>
    </div>
  );
}
